import React, { useState } from 'react';

const titleCaseValue = (value = '') => String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());

const normalizeDigest = (digest = null) => {
    if (!digest || typeof digest !== 'object') return null;
    const identity = digest.case_identity && typeof digest.case_identity === 'object' ? digest.case_identity : {};
    const canonicalFacts = Array.isArray(digest.canonical_facts) ? digest.canonical_facts.filter(Boolean) : [];
    const openQuestions = Array.isArray(digest.open_questions) ? digest.open_questions.filter(Boolean) : [];
    const importantDocuments = Array.isArray(digest.important_documents) ? digest.important_documents.filter(Boolean) : [];
    const recentActivity = Array.isArray(digest.recent_activity) ? digest.recent_activity.filter(Boolean) : [];
    const sourceCards = Array.isArray(digest.source_cards) ? digest.source_cards.filter(Boolean) : [];
    if (!identity.case_id && canonicalFacts.length === 0 && openQuestions.length === 0 && importantDocuments.length === 0 && recentActivity.length === 0) {
        return null;
    }
    return {
        ...digest,
        identity,
        canonicalFacts,
        openQuestions,
        importantDocuments,
        recentActivity,
        sourceCards,
        summary: digest.summary && typeof digest.summary === 'object' ? digest.summary : {},
    };
};

export const getCaseEvidenceDigestFromAnswer = (answer = null) => normalizeDigest(answer?.case_evidence_digest || null);

const sourceFromFact = (fact = {}, sourceCards = []) => {
    const fieldId = Number(fact?.field_id || fact?.custom_field_id || 0);
    const key = String(fact?.key || '').toLowerCase();
    if (fieldId > 0) {
        const match = sourceCards.find(source => Number(source?.field_id || source?.custom_field_id || 0) === fieldId);
        if (match) return match;
    }
    return sourceCards.find(source => String(source?.title || source?.label || '').toLowerCase().includes(key.replace(/_/g, ' '))) || null;
};

const sourceFromDocument = (document = {}, sourceCards = []) => {
    const fileId = Number(document?.file_id || 0);
    if (fileId > 0) {
        return sourceCards.find(source => Number(source?.file_id || 0) === fileId) || {
            source_type: 'document',
            file_id: fileId,
            title: document.title,
            file_name: document.title,
            excerpt: document.path || '',
        };
    }
    return null;
};

const sourceFromActivity = (activity = {}, sourceCards = []) => {
    const feedUpdateId = Number(activity?.feed_update_id || activity?.update_id || 0);
    if (feedUpdateId > 0) {
        return sourceCards.find(source => Number(source?.feed_update_id || source?.update_id || 0) === feedUpdateId) || {
            source_type: 'feed_update',
            feed_update_id: feedUpdateId,
            title: activity.title,
            excerpt: activity.excerpt || '',
        };
    }
    return null;
};

const formatConfidence = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return 'Grounded';
    if (numericValue > 0 && numericValue <= 1) return `${Math.round(numericValue * 100)}%`;
    if (numericValue > 1 && numericValue <= 100) return `${Math.round(numericValue)}%`;
    return 'Grounded';
};

const sourceButtonLabel = (source = null) => {
    if (!source) return '';
    return String(source.title || source.file_name || source.label || source.source_type || 'Source').trim() || 'Source';
};

const CaseEvidenceDigestReview = ({ digest, onCitationOpen }) => {
    const [activeScope, setActiveScope] = useState('all');
    const normalized = normalizeDigest(digest);
    if (!normalized) return null;

    const { identity, canonicalFacts, openQuestions, importantDocuments, recentActivity, sourceCards, summary } = normalized;
    const caseTitle = String(identity.case_name || (identity.case_id ? `Case ${identity.case_id}` : 'Case evidence')).trim();
    const evidenceRows = [
        ...canonicalFacts.slice(0, 24).map((fact, index) => {
            const source = sourceFromFact(fact, sourceCards);
            return {
                id: `fact-${fact?.key || index}`,
                type: 'Fact',
                detail: String(fact?.label || titleCaseValue(fact?.key || '') || 'Fact'),
                value: String(fact?.value || 'Present'),
                confidence: formatConfidence(fact?.confidence),
                source,
                scope: 'facts',
            };
        }),
        ...openQuestions.slice(0, 16).map((fact, index) => ({
            id: `question-${fact?.key || index}`,
            type: 'Open Question',
            detail: String(fact?.label || titleCaseValue(fact?.key || '') || 'Open question'),
            value: String(fact?.key || '').replace(/_/g, ' ') || 'Needs review',
            confidence: 'Missing',
            source: null,
            scope: 'questions',
        })),
        ...importantDocuments.slice(0, 16).map((document, index) => ({
            id: `document-${document?.file_id || index}`,
            type: 'Document',
            detail: String(document?.title || 'Document'),
            value: [titleCaseValue(document?.kind || ''), document?.path, document?.created_at].filter(Boolean).join(' · ') || 'Available',
            confidence: titleCaseValue(document?.kind || 'Document'),
            source: sourceFromDocument(document, sourceCards),
            scope: 'documents',
        })),
        ...recentActivity.slice(0, 12).map((activity, index) => ({
            id: `activity-${activity?.feed_update_id || index}`,
            type: 'Activity',
            detail: String(activity?.title || 'Activity update'),
            value: [titleCaseValue(activity?.type || ''), activity?.created_at, activity?.excerpt].filter(Boolean).join(' · ') || 'Available',
            confidence: 'Update',
            source: sourceFromActivity(activity, sourceCards),
            scope: 'activity',
        })),
    ];
    const scopeOptions = [
        { key: 'all', label: 'All', count: evidenceRows.length },
        { key: 'facts', label: 'Facts', count: canonicalFacts.length },
        { key: 'questions', label: 'Gaps', count: openQuestions.length },
        { key: 'documents', label: 'Docs', count: importantDocuments.length },
        { key: 'activity', label: 'Activity', count: recentActivity.length },
    ].filter(option => option.key === 'all' || option.count > 0);
    const activeScopeKey = scopeOptions.some(option => option.key === activeScope) ? activeScope : 'all';
    const visibleRows = activeScopeKey === 'all' ? evidenceRows : evidenceRows.filter(row => row.scope === activeScopeKey);
    const dataColumnWidth = 142;
    const tableWidth = 38 + (4 * dataColumnWidth);

    return (
        <section className="library-rag-native-table-preview case-evidence" aria-label="Case evidence digest" style={{ '--library-rag-native-table-width': `${tableWidth}px`, '--library-rag-native-data-column-width': `${dataColumnWidth}px` }}>
            <div className="library-rag-native-table-header">
                <div className="library-rag-native-table-heading">
                    <div className="library-rag-native-table-title" title={caseTitle}>{caseTitle}</div>
                    <div className="library-rag-native-table-meta">
                        {identity.case_type ? `${identity.case_type} · ` : ''}
                        {summary.present_fact_count || canonicalFacts.length} facts
                        {(summary.missing_fact_count || openQuestions.length) ? ` · ${summary.missing_fact_count || openQuestions.length} gaps` : ''}
                    </div>
                </div>
                <div className="library-rag-native-table-actions">
                    <div className="library-rag-native-table-switcher library-rag-case-evidence-scope" aria-label="Scope case evidence rows">
                        {scopeOptions.map((option) => (
                            <button
                                key={`case-evidence-scope-${option.key}`}
                                type="button"
                                className={option.key === activeScopeKey ? 'active' : ''}
                                onClick={() => setActiveScope(option.key)}
                                title={`${option.label}: ${option.count}`}
                                aria-label={`Show ${option.label.toLowerCase()} evidence rows`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <div className="library-rag-native-table-scroll">
                <table className="library-rag-native-table library-rag-case-evidence-table">
                    <colgroup>
                        <col className="library-rag-native-table-index-col" />
                        <col className="library-rag-native-table-data-col" />
                        <col className="library-rag-native-table-data-col" />
                        <col className="library-rag-native-table-data-col" />
                        <col className="library-rag-native-table-data-col" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th className="library-rag-native-table-index" aria-label="Row number"></th>
                            <th><span className="library-rag-native-cell-label">Detail</span></th>
                            <th><span className="library-rag-native-cell-label">Value</span></th>
                            <th><span className="library-rag-native-cell-label">Confidence</span></th>
                            <th><span className="library-rag-native-cell-label">Source</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleRows.map((row, rowIndex) => (
                            <tr key={`case-evidence-row-${row.id}`}>
                                <td className="library-rag-native-table-index">{rowIndex + 1}</td>
                                <td title={`${row.detail} · ${row.type}`}>
                                    <span className="library-rag-native-cell-value library-rag-case-evidence-detail">
                                        <strong>{row.detail}</strong>
                                        <small>{row.type}</small>
                                    </span>
                                </td>
                                <td title={row.value}><span className="library-rag-native-cell-value">{row.value}</span></td>
                                <td title={row.confidence}><span className="library-rag-native-cell-value">{row.confidence}</span></td>
                                <td>
                                    {row.source ? (
                                        <button type="button" className="library-rag-case-evidence-source" onClick={() => onCitationOpen?.(row.source)} title={sourceButtonLabel(row.source)}>
                                            Source
                                        </button>
                                    ) : <span className="library-rag-native-cell-value">—</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

export default CaseEvidenceDigestReview;
